// src/contexts/BusinessContext.tsx
import React, { createContext, useContext, useEffect, useState } from "react";
import { useAuth } from "./AuthContext"; // Assuming you have an AuthContext to get the current user
import { Business } from "@visitproof/types";
import { BusinessService } from "../services/BusinessService";
import { useAccount } from "./AccountContext";
import { collection, doc, onSnapshot } from "firebase/firestore";
import { db } from "../firebase";

interface BusinessContextProps {
  business: Business | null;
  loading: boolean;
  error: string | null;
  updateBusiness: (
    id: string,
    updatedData: Partial<Omit<Business, "id" | "createdAt" | "updatedAt">>,
  ) => Promise<void>;
}

const BusinessContext = createContext<BusinessContextProps | undefined>(
  undefined,
);

// Create a provider component
export const BusinessProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { account } = useAccount();
  const [business, setBusiness] = useState<any>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const { user } = useAuth();

  useEffect(() => {
      if (!account?.id || !user) {
        setLoading(false);
        return;
      }
      const docRef = doc(collection(db, "businesses"), account.id);
      const unsubscribe = onSnapshot(
        docRef,
        (docSnapshot) => {
          setLoading(false);
          if (docSnapshot.exists()) {
            setBusiness({ id: docSnapshot.id, ...docSnapshot.data() } as Business);
          } else {
            setError("Business not found.");
          }
        },
        (err) => {
          setError(`Failed to listen to user changes: ${err.message}`);
        },
      );
      return () => {
        unsubscribe();
      };
    }, [account, user]);

  const updateBusiness = async (
    businessId: string,
    updatedData: Partial<Omit<Business, "id" | "createdAt" | "updatedAt">>,
  ) => {
    await BusinessService.updateBusiness(businessId, updatedData);
  };

  return (
    <BusinessContext.Provider
      value={{ business, updateBusiness, loading, error }}
    >
      {children}
    </BusinessContext.Provider>
  );
};

// Custom hook for using the BusinessContext
export const useBusiness = (): BusinessContextProps => {
  const context = useContext(BusinessContext);
  if (context === undefined) {
    throw new Error("useBusiness must be used within an BusinessProvider");
  }
  return context;
};
